// Estado do jogador logado que o core resolve numa chamada só (GET /players/me):
// se ele pode entrar em jogo condicionado a aposta, e em que nível VIP está.
// Escada de níveis do core (VipLevelSeeder): 8 degraus, `order` 0 a 7, maior =
// melhor. Só o seeder escreve — a operação edita thresholds e bônus, nunca a
// ordem —, então `order` é número estável e é ele que identifica o nível aqui.
export type PlayerVipLevel = {
  // Rótulo do nível como o core escreve: 'VIP 0'..'VIP 6' (COM espaço) e
  // 'SIGNATURE', que não tem número. Desde 19/08/2026 é ele que VEM NO FIO — o
  // `vip` da resposta é esta string, e não mais um objeto.
  tag: string;
  // O NÍVEL. Casa 1:1 com o tier em VIP 0–6; `7` é o SIGNATURE, que não tem
  // número na tag. `null` num payload quebrado (tag que não casa com a escada)
  // — e aí vale a regra da casa: "não sei" cai na arte padrão, não no nível 0,
  // que é um nível de verdade.
  //
  // NÃO vem mais do core: é DERIVADO do texto da `tag` (`vipOrderFromTag`),
  // porque o `vip` achatado não carrega mais o número. Segue sendo ele, e não a
  // tag, quem escolhe a arte — o resto do front não muda.
  order: number | null;
  // Cor hex do nível no BACKOFFICE (badge, avatar, borda), editável pela
  // operação. O site NÃO desenha com ela: a arte dos cards vem do Figma e as duas
  // paletas divergem de propósito (o VIP 3 é verde no card e violeta aqui). O
  // custo é conhecido: trocar a cor no backoffice não muda o site — nunca mudou.
  //
  // Com o `vip` achatado ela some do fio e fica sempre `null`. Como ninguém
  // desenhava com ela, isso não tira nada da tela; o campo sobrevive só pelo
  // contrato antigo, que ainda é aceito.
  color: string | null;
  // WhatsApp do gerente dedicado deste tier (só vem preenchido a partir do
  // tier gerente — ver MANAGER_TIER em components/user-v2/Header.vue). `null`
  // pra quem não tem gerente ainda ou campo ausente/tipo inesperado.
  supportWhatsapp: string | null;
};

// Slugs das gamificações como o core as cataloga (CrmGamificationSeeder). São
// as CHAVES do mapa `gamifications` do /players/me: cada feature lê a sua. Um
// slug que sumir do mapa é gamificação DESATIVADA no backoffice — o front trata
// como não elegível, por contrato.
export const GAMIFICATION_SLUGS = {
  store: 'store',
  supporterCode: 'supporter_code',
  promocodes: 'promocodes',
  roulette: 'roulette',
  dailyCheckIn: 'daily_check_in',
} as const;

export type GamificationSlug =
  (typeof GAMIFICATION_SLUGS)[keyof typeof GAMIFICATION_SLUGS];

// Veredito do core pra UMA gamificação: o degrau mínimo que ela exige e se o
// jogador já chegou lá. O mínimo é editável no backoffice (CRM › Elegibilidade)
// sem deploy, por isso ele VEM no payload — a copy do aviso lê daqui, nunca de
// uma constante local.
export type PlayerGamificationEligibility = {
  // Slug CRM do degrau mínimo ('BRONZE LV 1', 'NIVEL 0'). `null` quando o core
  // não mandou string — o veredito ainda vale, só a copy perde o nome do degrau.
  minLevel: string | null;
  // Só `true` quando o core AFIRMA `true`.
  isEligible: boolean;
};

// O mapa inteiro, por slug. Chave ausente = gamificação desativada = inelegível.
export type PlayerGamifications = Record<string, PlayerGamificationEligibility>;

export type PlayerProfile = {
  userId: number;
  // Elegibilidade POR GAMIFICAÇÃO, decidida pelo nível CRM do jogador contra o
  // mínimo de cada feature. Substituiu o `is_eligible` da raiz (que dizia "já
  // apostou" e valia pra tudo). NÃO é monotônico: o nível sobe pelo webhook e
  // desce pelo reparo manual no backoffice, então é resolvido a cada leitura e
  // nunca hidrata do cache desta aba.
  //
  // `null` = CONTRATO QUEBRADO (campo ausente, array, string, payload truncado
  // — inclusive um core antigo que ainda manda o booleano na raiz). É "não sei",
  // e "não sei" não nega: os modais abrem normal e o core dá a palavra final no
  // POST. Objeto (mesmo `{}`) é veredito.
  gamifications: PlayerGamifications | null;
  // null = jogador comum, e só. Não há outra flag: `vip === null` é o único
  // sinal. Não é monotônico (nível sobe e desce), por isso o core resolve por
  // join a cada leitura em vez de gravar.
  //
  // A auto-exclusão NÃO cai mais aqui (PR #335): a leitura do perfil parou de
  // filtrar por status, porque a plataforma barra o login do autoexcluído — se a
  // request chegou, a exclusão já expirou, ainda que a linha em users_vips não
  // seja reescrita no vencimento. O que isso conserta é uma contradição no mesmo
  // payload (`vip: null` ao lado de `level: "vip_4"`), que ia fazer a regra de
  // divergência chamar sync-level em loop. Atribuição e movimentação de nível
  // seguem respeitando a exclusão — só a LEITURA do perfil mudou.
  //
  // O front memoiza o nível junto do "sim" mesmo assim, e por isso ele fica
  // congelado enquanto a ABA viver — decisão de produto, ver
  // handlers/fetch-player-profile.handler.
  vip: PlayerVipLevel | null;
  // Slug do nível no CRM como o CORE o guarda (a escada da Smartico espelhada
  // lá). É o lado "armazenado" da regra de divergência do sync-level: quando ele
  // discorda do que a Smartico reporta no widget, o front DENUNCIA a divergência
  // (stores/crm-level-sync.ts) — e para por aí. A rota não corrige nada e não
  // escreve neste campo: quem resolve é uma ação manual no backoffice, então o
  // valor só muda numa leitura futura do /players/me.
  //
  // `null` = o core ainda não tem registro — isso NÃO é divergência: quem cria o
  // registro que falta é o backfill do core, não uma chamada preventiva nossa a
  // cada login.
  level: string | null;
};
